import { useState, type FormEvent } from 'react';
import { getErrorMessage } from '../api/client';
import { createPresentation } from '../api/inventory';
import type { Ingredient, InventoryUnit, PurchasePresentation } from '../types/inventory';
import { compatibleUnitOptions } from '../utils/units';
import { formatMoney, formatUnitCost } from '../utils/costFormatting';
import { ProgressiveSection } from './ui/ProgressiveSection';
import { unitWordPlural, unitWordSingular } from '../utils/unitWording';
import { Modal } from './Modal';

export function PresentationQuickCreateModal({ ingredient, units, onClose, onCreated }: { ingredient: Ingredient; units: InventoryUnit[]; onClose: () => void; onCreated: (presentation: PurchasePresentation) => void }) {
  const [form, setForm] = useState({ name: '', quantity: '', unitId: ingredient.baseUnit.id, cost: '', supplierName: '' });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>();

  /**
   * El COSTO UNITARIO, que sale de dividir lo que se pagó entre lo que trae la presentación.
   *
   * No se guarda: la base solo guarda `cost` (lo que cuesta la presentación completa) y `quantity`
   * (lo que trae). Se muestra por dos razones: para que el dueño no tenga que dividir a mano antes de
   * escribir nada, y —más importante— para que vea que el sistema entendió lo mismo que él. Con una
   * «Caja de 9 Bolsas», «Costo por pieza» dejaba la duda de si la pieza era la caja o la bolsa; ver
   * «$0.0483 por ml» la resuelve sin explicar nada.
   */
  const unidad = units.find((item) => item.id === form.unitId);
  const unidadTexto = unitWordPlural(unidad);
  const unidadSingular = unitWordSingular(unidad);
  const contenido = Number(form.quantity);
  const pagado = Number(form.cost);
  const costoUnitario = contenido > 0 && Number.isFinite(pagado) && pagado >= 0 ? pagado / contenido : null;

  // ── El asistente: qué paso está abierto y cómo se avanza ─────────────────────────────────────
  type Paso = 'name' | 'unit' | 'quantity' | 'cost';
  const ORDEN: Paso[] = ['name', 'unit', 'quantity', 'cost'];
  const nombre = form.name.trim();
  const nombreTexto = nombre || 'esta presentación';
  // P-079 (Codex R1 · K10): «lleno» no es «válido». Completo es lo que el campo ya exigía (`min="0"`, contenido > 0): un
  // costo negativo o un contenido en cero se quedan abiertos con su mensaje, nunca en verde.
  const costoValido = form.cost !== '' && Number.isFinite(pagado) && pagado >= 0;
  const lleno: Record<Paso, boolean> = { name: Boolean(nombre), unit: Boolean(unidad), quantity: contenido > 0, cost: costoValido };
  const errores: Partial<Record<Paso, string>> = {
    quantity: form.quantity !== '' && !(contenido > 0) ? 'El contenido debe ser mayor que cero.' : undefined,
    cost: form.cost !== '' && !costoValido ? 'El costo no puede ser negativo.' : undefined,
  };
  const [open, setOpen] = useState<Paso | null>('name');
  // Codex R2 · R2: el paso siguiente toma el foco solo cuando se avanza a propósito (Enter, elegir la unidad) o al salir
  // del campo sin ir a otro control; si la persona tocó otro control (Proveedor, Cancelar), ese destino se respeta.
  const [focusNext, setFocusNext] = useState(true);
  /** Al contestar un paso se abre el SIGUIENTE que falte; si no falta ninguno, se cierra todo. */
  const avanzar = (desde: Paso, conFoco = true) => {
    if (!lleno[desde]) return;
    const siguiente = ORDEN.slice(ORDEN.indexOf(desde) + 1).find((p) => !lleno[p]) ?? ORDEN.find((p) => !lleno[p]) ?? null;
    setFocusNext(conFoco);
    setOpen(siguiente);
  };
  const salir = (desde: Paso) => (event: React.FocusEvent<HTMLInputElement>) => avanzar(desde, !event.relatedTarget);
  // Enter avanza en vez de enviar: el envío es del botón «Crear presentación».
  const enterAvanza = (desde: Paso) => (event: React.KeyboardEvent<HTMLInputElement>) => { if (event.key === 'Enter') { event.preventDefault(); avanzar(desde); } };
  // P-079: cada paso es una sección progresiva (la pieza común): abierta la que toca; las demás encogidas en verde
  // (completas) o en ámbar con «Llenar» (pendientes); «Cambiar» reabre con el valor puesto.
  const paso = (key: Paso, label: string, valor: string, pendiente: boolean, abierto: React.ReactNode) => (
    <ProgressiveSection busy={isSaving} complete={lleno[key]} error={errores[key]} key={key} label={label} onOpen={() => setOpen(key)} open={open === key} summary={valor} tone={pendiente ? 'missing' : 'done'}>
      {abierto}
    </ProgressiveSection>
  );

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (isSaving) return;
    setIsSaving(true);
    setError(undefined);
    try {
      const { presentation } = await createPresentation(ingredient.id, {
        name: form.name.trim(),
        quantity: Number(form.quantity),
        unitId: form.unitId,
        cost: Number(form.cost),
        currency: 'MXN',
        supplierName: form.supplierName.trim() || null,
      });
      onCreated(presentation);
    } catch (saveError) {
      setError(getErrorMessage(saveError, 'No fue posible crear la presentación.'));
    } finally {
      setIsSaving(false);
    }
  };

  return <Modal
    title="Nueva presentación"
    description={`Se agregará a ${ingredient.name} y quedará disponible de inmediato en este formulario.`}
    isBusy={isSaving}
    onClose={onClose}
    footer={<>
      <button className="button button--ghost" disabled={isSaving} onClick={onClose} type="button">Cancelar</button>
      <button className="button button--primary" disabled={isSaving || !form.name.trim() || !form.quantity || !form.cost} form="presentation-quick-create-form" type="submit">{isSaving ? 'Creando...' : 'Crear presentación'}</button>
    </>}
  >
    <form id="presentation-quick-create-form" onSubmit={(event) => void submit(event)}>
      {error && <div className="alert alert--error">{error}</div>}
      {/* Formulario GUIADO: un paso abierto a la vez; lo contestado se encoge a una línea con
          «Cambiar» (wiz-done, el mismo renglón que usa Capturar ticket) y la siguiente pregunta se
          lleva la pantalla. Las preguntas nombran lo ya escrito: la unidad elegida y el nombre. */}
      <div className="wiz-steps">
        {paso('name', 'Nombre', nombre || 'Pendiente', !nombre,
          <>
            <h3 className="wiz-question">¿Cómo se llama esta presentación?</h3>
            <div className="wiz-answers"><label className="field"><input aria-label="Nombre de la presentación" autoFocus={focusNext} placeholder="Ej.: Caja de 9 bolsas de 2 litros cada una" required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} onBlur={salir('name')} onKeyDown={enterAvanza('name')} /></label></div>
          </>)}
        {paso('unit', 'Unidad', unidad ? `${unidad.name} (${unidad.symbol})` : 'Pendiente', !unidad,
          <>
            <h3 className="wiz-question">¿En qué unidad viene {nombreTexto}?</h3>
            <div className="wiz-answers"><label className="field"><select aria-label="Unidad del contenido" autoFocus={focusNext} value={form.unitId} onChange={(event) => { setForm({ ...form, unitId: event.target.value }); avanzar('unit'); }}>{compatibleUnitOptions(units, ingredient.baseUnit, form.unitId).map(([id, text]) => <option key={id} value={id}>{text}</option>)}</select></label></div>
          </>)}
        {paso('quantity', 'Contenido', contenido > 0 ? `${contenido.toLocaleString('es-MX')} ${unidadTexto}` : 'Pendiente', !(contenido > 0),
          <>
            <h3 className="wiz-question">¿Cuántos {unidadTexto} trae {nombreTexto}?</h3>
            <div className="wiz-answers"><label className="field"><input aria-label={`Contenido en ${unidadTexto}`} autoFocus={focusNext} min="0" required step="any" type="number" value={form.quantity} onChange={(event) => setForm({ ...form, quantity: event.target.value })} onBlur={salir('quantity')} onKeyDown={enterAvanza('quantity')} /><small className="cost-mode-hint">El total que trae la presentación completa (ej. 18 si son 9 bolsas de 2 litros).</small></label></div>
          </>)}
        {paso('cost', 'Costo aproximado', Number.isFinite(pagado) && form.cost !== '' ? formatMoney(String(pagado)) : 'Pendiente', form.cost === '',
          <>
            <h3 className="wiz-question">Costo aproximado de {nombreTexto}</h3>
            <div className="wiz-answers"><label className="field"><input aria-label="Costo aproximado de la presentación" autoFocus={focusNext} min="0" required step="any" type="number" value={form.cost} onChange={(event) => setForm({ ...form, cost: event.target.value })} onBlur={salir('cost')} onKeyDown={enterAvanza('cost')} /><small className="cost-mode-hint">Lo que pagas por la presentación completa, con impuestos. Puede cambiar en cada compra.</small></label></div>
          </>)}
      </div>
      {/* La división, hecha. Siempre visible y se actualiza sola: es el «eco» del asistente. */}
      <p className={costoUnitario === null ? 'presentation-unit-cost presentation-unit-cost--pending' : 'presentation-unit-cost'}>
        {costoUnitario === null
          ? <>Escribe el contenido y el costo y aquí verás cuánto sale cada {unidadSingular}.</>
          : <>Sale a <strong>{formatUnitCost(costoUnitario)} por {unidad?.symbol ?? ''}</strong> <span>· {formatUnitCost(pagado)} entre {contenido.toLocaleString('es-MX')} {unidad?.symbol ?? ''}</span></>}
      </p>
      <label className="field"><span>Proveedor opcional</span><input value={form.supplierName} onChange={(event) => setForm({ ...form, supplierName: event.target.value })} /></label>
    </form>
  </Modal>;
}
